import Control from '../../1. Core/Control';
import DataTable from '../../1. Core/Controls/DataTable/DataTable';
import TopAppBar from '../../1. Core/Controls/TopAppBar/TopAppBar';
import Delegate from '../../1. Core/Delegate';
import NP from '../../1. Core/NP';
import {ServerAPI} from '../../1. Core/ServerAPI';
import Menu from '../../1. Core/Controls/Menu/Menu';
import ListItem from '../../1. Core/Controls/ListBox/ListItem';
import Dialog from '../../1. Core/Controls/Dialog/Dialog';
import CheckBox from '../../1. Core/Controls/CheckBox/CheckBox';

interface UserAchievementClaimRow {
	tierId: number;
	chainKey: string;
	kind: string;
	iconKey: string;
	thresholdText: string;
	globalSort: number;
	claimedAt: string;
	Title: string;
}

interface AchievementTierRow {
	tierId: number;
	chainKey: string;
	kind: string;
	iconKey: string;
	thresholdText: string;
	globalSort: number;
	Title: string;
}

interface UserAchievementsResponse {
	userId: number;
	email: string;
	firstName: string;
	lastName: string;
	claims: UserAchievementClaimRow[];
}

export default class UserAchievementsPanel extends Control {
	private _topAppBar: TopAppBar;
	private EvClosed: Delegate;
	private _dataTable: DataTable;
	private _dataWithFiltersNode: HTMLElement;
	private _UserId: number;
	private _allTiers: AchievementTierRow[];

	constructor(settings: object) {
		super(settings);
		this._render();
	}

	_initFields(settings: object) {
		this._UserId = null;
		this._allTiers = [];
		this.EvClosed = new Delegate();
		super._initFields(settings);
	}

	_render() {
		this.np_render();

		this._topAppBar = new TopAppBar({
			Class: 'top-app-bar--no-shadow top-app-bar--light library-box-list__app-bar',
			Caption: 'User achievements',
			ParentNode: this._root,
			ShowSearchButton: false,
			MainButton: {
				FontImage: NP.FontImages.arrow_back,
				EvClick: () => this.EvClosed.fire(this)
			},
			RightItems: [{
				ToolTip: 'Add achievement',
				FontImage: NP.FontImages.add,
				EvClick: () => this.showAddDialog()
			}]
		});

		this._dataWithFiltersNode = NP.createNode(this._root, 'data-with-filters-container');

		this._dataTable = new DataTable({
			ParentNode: this._dataWithFiltersNode,
			ShowSettingsButton: true,
			Class: 'app-list--avatars mdc-data-table--margin data-table-battle',
			MultiSelect: false,
			ShowRowIndex: false,
			EnableSort: false,
			Width: this._Width,
			Height: this._Height - 64,
			Columns: [{
				Content: 'Tier',
				PropertyContent: 'tierId',
				Width: '60px'
			}, {
				Content: 'Title',
				PropertyContent: 'Title',
				Width: 'calc(100% - 60px - 120px - 100px - 120px - 180px)'
			}, {
				Content: 'Chain',
				PropertyContent: 'chainKey',
				Width: '120px'
			}, {
				Content: 'Kind',
				PropertyContent: 'kind',
				Width: '100px'
			}, {
				Content: 'Icon',
				PropertyContent: 'iconKey',
				Width: '120px'
			}, {
				Content: 'Claimed at',
				PropertyContent: 'claimedAt',
				Width: '180px'
			}],
			ShowPaginator: false,
			EvItemContextMenu: async (s: object, e: { Event: Event, Item: UserAchievementClaimRow }) => {
				const item = e.Item;
				const menu = new Menu({
					Items: [{
						FontImage: NP.FontImages.delete,
						Content: 'Remove',
						Value: 'Remove'
					}],
					EvItemSelected: async (sender: Menu, args: { Item: ListItem }) => {
						if (args.Item.getValue() !== 'Remove') {
							return;
						}
						NP.showConfirm({
							Content: `Remove achievement #${item.tierId} and all following in chain "${item.chainKey}"?`,
							EvOkButtonClick: async () => {
								await ServerAPI.delet(`/achievements/v2/admin/user/${this._UserId}/claim/${item.tierId}`);
								this.refresh();
							}
						}).show();
					}
				});
				menu.showContextMenu(e.Event);
			}
		});

		this.refresh();
	}

	getTopAppBar() {
		return this._topAppBar;
	}

	getUserId() {
		return this._UserId;
	}

	formatTierTitle(tier: { tierId: number; thresholdText?: string; chainKey: string }): string {
		if (tier.thresholdText) {
			return tier.thresholdText;
		}
		return `${tier.chainKey} #${tier.tierId}`;
	}

	formatClaimedAt(value: string): string {
		if (!value) {
			return '';
		}
		const date = new Date(value);
		return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
	}

	async showAddDialog() {
		if (!this._allTiers.length) {
			this._allTiers = (await ServerAPI.get<AchievementTierRow[]>('/achievements/v2/admin/tiers'))
				.map((tier) => ({
					...tier,
					Title: this.formatTierTitle(tier)
				}));
		}

		const claimedIds = new Set(this._dataTable.getRows().map((row: UserAchievementClaimRow) => row.tierId));
		const available = this._allTiers.filter((tier) => !claimedIds.has(tier.tierId));

		if (!available.length) {
			NP.showMessage({ Content: 'All achievements are already granted' });
			return;
		}

		const picker = new DataTable({
			Class: 'app-list--avatars',
			EnableSort: false,
			ShowPaginator: false,
			Height: 360,
			Columns: [{
				Content: 'Tier',
				PropertyContent: 'tierId',
				Width: '60px'
			}, {
				Content: 'Title',
				PropertyContent: 'Title',
				Width: '50%'
			}, {
				Content: 'Chain',
				PropertyContent: 'chainKey',
				Width: '20%'
			}, {
				Content: 'Kind',
				PropertyContent: 'kind',
				Width: '15%'
			}],
			EvItemClick: async (s: object, e: { Item: AchievementTierRow }) => {
				const tier = e.Item;
				const body = document.createElement('div');
				const textNode = document.createElement('div');
				textNode.textContent = `Grant achievement #${tier.tierId}?`;
				body.appendChild(textNode);
				const grantRewards = new CheckBox({
					Content: 'Grant rewards',
					Checked: true
				});
				grantRewards.putToNode(body);
				NP.showConfirm({
					Content: body,
					EvOkButtonClick: async () => {
						const ok = await ServerAPI.post<boolean>(
							`/achievements/v2/admin/user/${this._UserId}/grant/${tier.tierId}`,
							{ grantRewards: grantRewards.getValue() }
						);
						if (!ok) {
							NP.showMessage({ Content: 'Could not grant achievement' });
							return;
						}
						dlg.hide();
						this.refresh();
					}
				}).show();
			}
		});
		picker.setRows(available);

		const dlg = new Dialog({
			Caption: 'Add achievement',
			ShowHeader: true,
			Width: 720,
			Content: [picker]
		});
		dlg.show();
	}

	setHeight(value: number) {
		super.setHeight(value);
		if (this._dataTable) {
			this._dataTable.setHeight(value - 64);
		}
	}

	setWidth(value: number) {
		super.setWidth(value);
		if (this._dataTable) {
			this._dataTable.setWidth(value);
		}
	}

	async refresh() {
		const res = await ServerAPI.get<UserAchievementsResponse>(`/achievements/v2/admin/user/${this._UserId}`);
		const name = `${res.email} ${res.firstName || ''} ${res.lastName || ''}`.trim();
		this._topAppBar.setCaption(`Achievements: ${name}`);

		this._dataTable.setRows(res.claims.map((claim) => ({
			...claim,
			Title: this.formatTierTitle(claim),
			claimedAt: this.formatClaimedAt(claim.claimedAt)
		})));
	}
}
